import { APP_TIMEZONE } from '@kominiarz/shared';
import { DateTime } from 'luxon';

/** Calendar views as they appear in the URL (Polish, user-facing). */
export type CalendarView = 'lista' | 'dzien' | 'tydzien' | 'miesiac';
export const CALENDAR_VIEWS: CalendarView[] = ['lista', 'dzien', 'tydzien', 'miesiac'];
export const VIEW_LABELS: Record<CalendarView, string> = {
  lista: 'Lista',
  dzien: 'Dzień',
  tydzien: 'Tydzień',
  miesiac: 'Miesiąc',
};
/** How many days the list (agenda) shows. */
export const AGENDA_DAYS = 7;

export const local = (date: string) => DateTime.fromISO(date, { zone: APP_TIMEZONE }).setLocale('pl');

export interface DateRange {
  /** Inclusive start (local midnight). */
  start: DateTime;
  /** Exclusive end (local midnight). */
  end: DateTime;
}

/** The visible days of a view anchored at `date`; matches what FullCalendar draws. */
export function viewRange(view: CalendarView, date: string): DateRange {
  const d = local(date).startOf('day');
  switch (view) {
    case 'dzien':
      return { start: d, end: d.plus({ days: 1 }) };
    case 'tydzien': {
      const start = d.startOf('week');
      return { start, end: start.plus({ weeks: 1 }) };
    }
    case 'miesiac': {
      // FullCalendar's month grid: six full weeks starting on the Monday on/before the 1st.
      const start = d.startOf('month').startOf('week');
      return { start, end: start.plus({ weeks: 6 }) };
    }
    case 'lista':
    default:
      return { start: d, end: d.plus({ days: AGENDA_DAYS }) };
  }
}

/** Where the arrows move: by a day, a week or a month. */
export function shiftDate(view: CalendarView, date: string, direction: 1 | -1): string {
  const d = local(date);
  const step = view === 'dzien' ? { days: 1 } : view === 'miesiac' ? { months: 1 } : { weeks: 1 };
  return (direction === 1 ? d.plus(step) : d.minus(step)).toISODate()!;
}

/** "21–27 października 2026", "27 października – 2 listopada 2026", "październik 2026". */
export function periodTitle(view: CalendarView, date: string): string {
  const d = local(date);
  if (view === 'dzien') return d.toFormat('EEEE, d MMMM yyyy');
  if (view === 'miesiac') return d.toFormat('LLLL yyyy');
  const { start, end } = viewRange(view, date);
  const last = end.minus({ days: 1 });
  if (start.month === last.month) return `${start.day}–${last.toFormat('d MMMM yyyy')}`;
  if (start.year === last.year) return `${start.toFormat('d MMMM')} – ${last.toFormat('d MMMM yyyy')}`;
  return `${start.toFormat('d MMMM yyyy')} – ${last.toFormat('d MMMM yyyy')}`;
}

export function todayLocal(): string {
  return DateTime.now().setZone(APP_TIMEZONE).toISODate()!;
}

/** "9:30–11:00" style range in Warsaw time. */
export function timeRange(startIso: string, endIso: string): string {
  const s = DateTime.fromISO(startIso).setZone(APP_TIMEZONE);
  const e = DateTime.fromISO(endIso).setZone(APP_TIMEZONE);
  return `${s.toFormat('HH:mm')}–${e.toFormat('HH:mm')}`;
}

/** "środa, 21 października" → "Środa, 21 października" (weekday-first dates start a line). */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
